import { usePrevious } from '@sequencehq/utils'
import { useEffect, useMemo, useState } from 'react'
import { NodeType } from 'Automations/nodeDefinitions/types'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

type Task = Dashboardv99990101Api.Automations.AutomationExecutionTask
type ManualTask = Dashboardv99990101Api.Automations.ManualTask

type Node = {
  nodeId: string
  nodeType: NodeType
  tasks: Array<Task>
}

type TimelinePartition = {
  firstNode: Node | undefined
  collapsibleNodes: Array<Node>
  pinnedNodes: Array<Node>
}

const isActiveNode = (node: Node): boolean =>
  node.tasks.some(
    task => task.status === 'IN_PROGRESS' || task.status === 'SCHEDULED'
  )

const hasReviewableTask = (
  node: Node,
  reviewableTasks: Array<ManualTask>
): boolean => {
  const taskIds = new Set(node.tasks.map(t => t.taskId))
  return reviewableTasks.some(
    rt => rt.status === 'PENDING' && taskIds.has(rt.executorTaskId)
  )
}

const needsPinning = (
  node: Node,
  reviewableTasks: Array<ManualTask>
): boolean => isActiveNode(node) || hasReviewableTask(node, reviewableTasks)

const partitionNodes = (
  nodes: Array<Node>,
  reviewableTasks: Array<ManualTask>
): TimelinePartition => {
  const firstNode = nodes[0] as Node | undefined
  const remainingNodes = nodes.slice(1)

  const pinnableNodes = remainingNodes.filter(n =>
    needsPinning(n, reviewableTasks)
  )
  const completedNodes = remainingNodes.filter(
    n => !needsPinning(n, reviewableTasks)
  )

  if (pinnableNodes.length === 0) {
    const finalNode = completedNodes.pop()
    return {
      firstNode,
      collapsibleNodes: completedNodes,
      pinnedNodes: finalNode ? [finalNode] : []
    }
  }

  if (pinnableNodes.length === 1) {
    return {
      firstNode,
      collapsibleNodes: completedNodes,
      pinnedNodes: pinnableNodes
    }
  }

  const reviewableNodes = pinnableNodes.filter(n =>
    hasReviewableTask(n, reviewableTasks)
  )

  if (reviewableNodes.length === 0) {
    return {
      firstNode,
      collapsibleNodes: completedNodes,
      pinnedNodes: pinnableNodes
    }
  }

  const reviewableSet = new Set(reviewableNodes.map(n => n.nodeId))
  return {
    firstNode,
    collapsibleNodes: remainingNodes.filter(n => !reviewableSet.has(n.nodeId)),
    pinnedNodes: reviewableNodes
  }
}

export const useExecutionTimeline = ({
  nodes,
  isRunning = false,
  reviewableTasks = [],
  executionId
}: {
  nodes: Array<Node>
  isRunning?: boolean
  reviewableTasks?: Array<ManualTask>
  executionId?: string | null
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const previousExecutionId = usePrevious(executionId)
  useEffect(() => {
    if (previousExecutionId !== executionId) {
      setIsExpanded(false)
    }
  }, [previousExecutionId, executionId])

  const { firstNode, collapsibleNodes, pinnedNodes } = useMemo(
    () => partitionNodes(nodes, reviewableTasks),
    [nodes, reviewableTasks]
  )

  const hasCollapsibleNodes = collapsibleNodes.length > 0

  const toggle = () => setIsExpanded(prev => !prev)

  return {
    firstNode,
    collapsibleNodes,
    pinnedNodes,
    hasCollapsibleNodes,
    isExpanded,
    toggle,
    orderedNodes: nodes,
    showWaitingItem: isRunning && pinnedNodes.length === 0
  }
}
